'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../_components/_ui/background-beams'
import Footer from '../_components/_ui/Footer'

const legalPages = [
    { 
        name: 'Privacy Policy',
        href: '/legal/privacy',
        description: "See how we're reinventing data minimalism. Spoiler: We store almost nothing! 🤓",
        icon: "🛡️",
        color: "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20"
    },
    { 
        name: 'Terms of Service',
        href: '/legal/terms',
        description: "Plain English, no gotchas. Just straightforward terms about how we work together.",
        icon: "📜",
        color: "bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/20"
    },
    { 
        name: 'Cookie Policy',
        href: '/legal/cookies',
        description: "The only cookies we care about are the ones that make your experience better.",
        icon: "🍪",
        color: "bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20"
    },
    { 
        name: 'Data Processing',
        href: '/legal/dpa',
        description: "Summit tier users: Learn about our secure data hosting and processing practices.",
        icon: "🔒",
        color: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/20"
    },
]

export default function LegalNav() {
    const pathname = usePathname()

    return (
        <div className="min-h-screen w-full relative flex flex-col">
            <BackgroundBeams />
            <div className="relative z-10 flex-grow">
                {/* Hero Section */}
                <div className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-2xl sm:text-3xl font-bold text-white leading-tight"
                        >
                            Legal & Privacy Center
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-sm text-gray-400 mt-2"
                        >
                            Transparency is in our DNA. Choose a topic to learn more.
                        </motion.p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {legalPages.map((page, index) => (
                            <motion.div
                                key={page.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Link href={page.href}>
                                    <div className={`p-6 rounded-xl border backdrop-blur-sm transition-all duration-300 ${page.color}`}>
                                        <div className="flex items-center gap-4">
                                            <span className="text-3xl">{page.icon}</span>
                                            <div>
                                                <h2 className="text-lg font-semibold text-white mb-2">
                                                    {page.name}
                                                </h2>
                                                <p className="text-sm text-gray-300">
                                                    {page.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    {/* Trust Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                        className="mt-12 p-6 rounded-xl border border-indigo-500/20 bg-indigo-500/10 backdrop-blur-sm"
                    >
                        <h3 className="text-lg font-semibold text-white mb-2">
                            Built on Trust & Transparency 🤝
                        </h3>
                        <p className="text-sm text-gray-300">
                            At Autofoc.us, we believe in being upfront about how we handle your data. 
                            We're building a platform that respects your privacy while delivering an amazing experience. 
                            No hidden agendas, just honest service.
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}
